import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/packages/docs-site/src/components/containers/MdxLayout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>{`Organization`}</h2>
    <ul>
      <li parentName="ul">{`every component has a dedicated directory under `}<inlineCode parentName="li">{`./packages/components/src`}</inlineCode></li>
      <li parentName="ul">{`includes an `}<inlineCode parentName="li">{`index.js`}</inlineCode>{`; see `}<a parentName="li" {...{
          "href": "#exports"
        }}>{`exports`}</a>{` below`}</li>
      <li parentName="ul">{`includes a `}<inlineCode parentName="li">{`README.mdx`}</inlineCode></li>
      <li parentName="ul">{`may include other files that are not publicly exported in the `}<inlineCode parentName="li">{`index.js`}</inlineCode></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`.
└── packages
    └── components
        └── src
            ├── index.js
            ├── README.mdx
            ├── otherInternalOnlyFunctionality.js
            └── SpacedGroup.js
`}</code></pre>
    <h2>{`Exports`}</h2>
    <p>{`The component directory's `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` file export the publicly consumable portions of the component. In other words, it should never be required to import from a file within the component's directory other than the `}<inlineCode parentName="p">{`index.js`}</inlineCode>{` file.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Component directories should `}<strong parentName="p">{`always`}</strong>{` export all items as `}<strong parentName="p">{`named exports`}</strong>{`.`}</p>
    </blockquote>
    <h3>{`Example `}<inlineCode parentName="h3">{`index.js`}</inlineCode></h3>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`export { default as MainComponent } from './MainComponent';
export { default as SecondaryComponent } from './SecondaryComponent';
`}</code></pre>
    <p>{`Occassionally there will be a group of components that work together and are only relevant when used with each other (example: a list and list items). In these cases, ensure the primary component is a named export. Prefix secondary components with primary component name. As an example, in the case of `}<inlineCode parentName="p">{`List`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`export { List } from './List';
export { default as ListItem } from './Item';
export { default as ListItemText } from './ItemText';
`}</code></pre>
    <h2>{`Styling`}</h2>
    <p>{`Component styling is powered by our implementation using `}<a parentName="p" {...{
        "href": "https://fela.js.org"
      }}>{`fela`}</a>{`.`}</p>
    <ul>
      <li parentName="ul">{`use `}<inlineCode parentName="li">{`createComponent`}</inlineCode>{` function to create styled components (`}<a parentName="li" {...{
          "href": "/components/StyleProvider"
        }}>{`see StyleProvider component`}</a>{`)`}</li>
    </ul>
    <blockquote>
      <p parentName="blockquote">{`See `}<a parentName="p" {...{
          "href": "/components/StyleProvider#createcomponent"
        }}>{`createComponent`}</a>{` and `}<a parentName="p" {...{
          "href": "/components/StyleProvider#themeprovider-component"
        }}>{`ThemeProvider`}</a>{` for more a more in-depth explanation.`}</p>
    </blockquote>
    <h3>{`Using `}<inlineCode parentName="h3">{`StyleProvider`}</inlineCode>{` and `}<inlineCode parentName="h3">{`createComponent`}</inlineCode>{` Example`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const Button = createComponent(
  () => ({
    color: 'blue',
  }),
  'div',
);

const SaveButton = ({ children }) => <Button>{children}</Button>;
`}</code></pre>
    <h2>{`Required PropTypes`}</h2>
    <ul>
      <li parentName="ul">{`components declare appropriate `}<inlineCode parentName="li">{`propTypes`}</inlineCode>{` and also comments on each `}<inlineCode parentName="li">{`propType`}</inlineCode></li>
      <li parentName="ul">{`components declare appropraite `}<inlineCode parentName="li">{`defaultProps`}</inlineCode></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const MyComponent = ({text}) => <span>Hello {text}.</span>;

MyComponent.propTypes = {
  /** Inner text */
  text: PropTypes.color,
};
MyComponent.defaultProps: {
  text: 'world',
};
`}</code></pre>
    <h2>{`ThemeDefinition`}</h2>
    <p>{`Not all components are theme-enabled; however, those that are should declare a `}<inlineCode parentName="p">{`ThemeDefinition`}</inlineCode>{` and `}<inlineCode parentName="p">{`defaultThemeValues`}</inlineCode>{`.`}</p>
    <blockquote>
      <p parentName="blockquote">{`See `}<a parentName="p" {...{
          "href": "/components/StyleProvider#createcomponent"
        }}>{`createComponent`}</a>{` and `}<a parentName="p" {...{
          "href": "/components/StyleProvider#themeprovider-component"
        }}>{`ThemeProvider`}</a>{` for more details on how to consume the theme in your component.`}</p>
    </blockquote>
    <ul>
      <li parentName="ul">{`components declare appropriate `}<inlineCode parentName="li">{`themeDefintion`}</inlineCode>{` and also comment descriptions for each value; similar to `}<inlineCode parentName="li">{`propTypes`}</inlineCode>{`, but describes the theme-able configuration properites of the component`}</li>
      <li parentName="ul">{`components declare appropriate `}<inlineCode parentName="li">{`defaultThemeValues`}</inlineCode></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`const MyComponent = () => <span>Hello World</span>;

MyComponent.themeDefintion = {
  /** Text color */
  color: PropTypes.color,
};
MyComponent.defaultThemeValues: {
  color: 'lightgray',
};
`}</code></pre>
    <h2>{`Further Reading`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/getting-started/document-components"
        }}>{`Document Components`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      